import { toast } from 'sonner';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import { Form, Field } from 'src/components/hook-form';

import { useBillingProfile } from './use-billing-profile';
import {
  SA_REGIONS,
  createBillingProfileSchema,
  toBillingProfileFormValues,
  BILLING_PROFILE_REQUIRED_FIELDS,
} from './billing-profile.config';

import type { BillingProfileFormValues } from './billing-profile.config';

// ----------------------------------------------------------------------

type Props = {
  canEdit?: boolean;
  /** Narrow layout (one column), e.g. inside the payment dialog. */
  compact?: boolean;
  submitLabel?: string;
  onSaved?: () => void;
  onCancel?: () => void;
  /** Tenant to edit, for Super Admin on a tenant's page; defaults to the user's own tenant. */
  tenantId?: string;
};

export function BillingProfileForm({
  canEdit = true,
  compact = false,
  submitLabel,
  onSaved,
  onCancel,
  tenantId,
}: Props) {
  const { t } = useTranslate('translation');
  const { profile, save, isSaving } = useBillingProfile({ tenantId });

  const schema = useMemo(() => createBillingProfileSchema(t), [t]);
  const values = useMemo(() => toBillingProfileFormValues(profile), [profile]);

  const methods = useForm<BillingProfileFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: values,
    values,
  });

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isDirty },
  } = methods;
  const isSaudi = watch('countryCode') === 'SA';

  const onSubmit = handleSubmit(async (data) => {
    try {
      await save(data);
      toast.success(t('BILLING_PROFILE_SAVED'));
      onSaved?.();
    } catch (error) {
      console.error('Error saving billing profile:', error);
      toast.error(t('BILLING_PROFILE_SAVE_FAILED'));
    }
  });

  const label = (key: keyof BillingProfileFormValues, text: string) =>
    BILLING_PROFILE_REQUIRED_FIELDS.includes(key) ? `${t(text)} *` : t(text);

  const fieldProps = {
    disabled: !canEdit || isSaving,
    slotProps: { inputLabel: { shrink: true } },
  };

  const renderSection = (title: string, children: React.ReactNode) => (
    <Stack spacing={2}>
      <Typography variant="subtitle2" color="text.secondary">
        {t(title)}
      </Typography>
      <Box
        sx={{
          rowGap: 3,
          columnGap: 2,
          display: 'grid',
          gridTemplateColumns: compact
            ? 'repeat(1, 1fr)'
            : { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
        }}
      >
        {children}
      </Box>
    </Stack>
  );

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Stack spacing={3}>
        {renderSection(
          'BILLING_PROFILE_SECTION_COMPANY',
          <>
            <Field.Text
              name="name"
              label={label('name', 'BILLING_PROFILE_NAME_AR')}
              placeholder="شركة الاختبار"
              {...fieldProps}
              slotProps={{ ...fieldProps.slotProps, htmlInput: { dir: 'rtl' } }}
            />
            <Field.Text
              name="nameEn"
              label={label('nameEn', 'BILLING_PROFILE_NAME_EN')}
              placeholder="Test Company"
              {...fieldProps}
            />
            <Field.Text
              name="vatNumber"
              label={label('vatNumber', 'BILLING_PROFILE_VAT_NUMBER')}
              placeholder="300000000000003"
              {...fieldProps}
            />
            <Field.Phone
              name="mobile"
              label={label('mobile', 'BILLING_PROFILE_MOBILE')}
              country="SA"
              {...fieldProps}
            />
          </>
        )}

        {renderSection(
          'BILLING_PROFILE_SECTION_ADDRESS',
          <>
            <Field.Text
              name="street"
              label={label('street', 'BILLING_PROFILE_STREET')}
              {...fieldProps}
            />
            <Field.Text
              name="buildingNumber"
              label={label('buildingNumber', 'BILLING_PROFILE_BUILDING_NUMBER')}
              placeholder="1234"
              {...fieldProps}
            />
            <Field.Text
              name="plotIdentification"
              label={label('plotIdentification', 'BILLING_PROFILE_PLOT_ID')}
              placeholder="5678"
              {...fieldProps}
            />
            <Field.Text
              name="neighborhood"
              label={label('neighborhood', 'BILLING_PROFILE_NEIGHBORHOOD')}
              {...fieldProps}
            />
            <Field.Text name="city" label={label('city', 'CITY')} {...fieldProps} />
            <Field.Text
              name="zip"
              label={label('zip', 'BILLING_PROFILE_ZIP')}
              placeholder="12211"
              {...fieldProps}
            />
            <Field.CountrySelect
              name="countryCode"
              label={label('countryCode', 'COUNTRY')}
              getValue="code"
              disableClearable
              disabled={fieldProps.disabled}
              onChange={(_event: unknown, newValue: string) => {
                // Region codes differ per country, so reset the region when the country changes.
                setValue('countryCode', newValue, { shouldValidate: true, shouldDirty: true });
                setValue('stateCode', '', { shouldDirty: true });
              }}
            />
            {isSaudi ? (
              <Field.Select
                name="stateCode"
                label={label('stateCode', 'BILLING_PROFILE_REGION')}
                {...fieldProps}
              >
                {SA_REGIONS.map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    {t(region.label)}
                  </MenuItem>
                ))}
              </Field.Select>
            ) : (
              <Field.Text
                name="stateCode"
                label={label('stateCode', 'BILLING_PROFILE_REGION')}
                {...fieldProps}
              />
            )}
          </>
        )}

        <Typography variant="caption" color="text.secondary">
          {t('BILLING_PROFILE_EXISTING_FIELDS_NOTE')}
        </Typography>

        {canEdit && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {onCancel && (
              <Button variant="outlined" color="inherit" onClick={onCancel} disabled={isSaving}>
                {t('CANCEL')}
              </Button>
            )}
            {/* Enabled only after the user changes something; the form resets to the saved values after a save. */}
            <Button type="submit" variant="contained" disabled={isSaving || !isDirty}>
              {isSaving ? t('SAVING') : (submitLabel ?? t('SAVE_CHANGES'))}
            </Button>
          </Stack>
        )}
      </Stack>
    </Form>
  );
}
